import {
  type ConfirmButtonProps,
  ConfirmButton,
} from "@/v2-backoffice-client/components/button"
import { useTranslationPendingRevert } from "@/v2-backoffice-client/translation/pending/translation.pending.revert"
import {
  isRevertable,
  type PendingChangeRow,
} from "@/v2-backoffice-client/translation/pending/translation.pending.utils"

const REVERT_MANY_CONTENT =
  "Restore every selected text line to its previously published state (additions are deleted). History is kept; nothing ships until the next publish."

export type TranslationPendingRevertManyButtonProps = Omit<
  ConfirmButtonProps,
  "onConfirm" | "content" | "confirmContent"
> & {
  rows: PendingChangeRow[]
  onReverted?(): void
}

// Reverts the table's current selection. The selection toolbar owns the
// reset, so it passes it through onReverted.
function TranslationPendingRevertManyButton(
  props: TranslationPendingRevertManyButtonProps,
) {
  const { rows, onReverted, children, disabled, ...buttonProps } = props

  const { revertRows, loading } = useTranslationPendingRevert({ onReverted })

  // Settings changes are already live in this environment, so they have no
  // published state to roll back to. Selecting a mixed set is normal (the
  // table groups by code) — revert what can be reverted and say what was
  // left, rather than silently dropping rows or refusing the whole batch.
  const revertableRows = rows.filter(isRevertable)
  const skipped = rows.length - revertableRows.length

  return (
    <ConfirmButton
      variant="destructive"
      isStacked
      iconSrc="undo"
      content={REVERT_MANY_CONTENT}
      confirmContent={
        `Revert ${revertableRows.length} text change(s) to the previously published state?` +
        (skipped > 0
          ? ` The ${skipped} settings change(s) in the selection stay as they are — they are already live and are edited on the feature itself.`
          : "")
      }
      confirmVariant="destructive"
      data-feature-code="UI_BackofficeTranslation_Pending_RevertSelected"
      isLoading={loading}
      disabled={disabled ?? (loading || revertableRows.length === 0)}
      onConfirm={() => revertRows(revertableRows)}
      {...buttonProps}
    >
      {children ?? "Revert selected"}
    </ConfirmButton>
  )
}

export default TranslationPendingRevertManyButton
